"use client";

import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import { ChevronDown, Plus, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Label, ListBox, ListBoxItem, MenuTrigger, Popover, Text } from "react-aria-components";
type OptionLabelValue = {
  value: string;
  label: string;
};
type GroupedOptionLabelValue = {
  label: string;
  value: string;
  options: OptionLabelValue[];
};
type SelectInputProps = {
  label?: string;
  description?: string;
  errorMessage?: string;
  options: (GroupedOptionLabelValue | OptionLabelValue)[];
  placeholder?: string;
  className?: string;
  multiple?: boolean;
  clearable?: boolean;
  creatable?: boolean;
  createOptionText?: string;
  onCreateOption?: (inputValue: string) => OptionLabelValue | Promise<OptionLabelValue>;
  createOption?: (inputValue: string) => OptionLabelValue | Promise<OptionLabelValue>;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  disabled?: boolean;
  variant?: "outline" | "underline";
  maxVisibleTags?: number;
  inputProps?: React.ComponentProps<typeof Input>;
};
function SelectInput({
  label,
  description,
  errorMessage,
  options,
  placeholder = "Select an option",
  className,
  multiple = false,
  clearable = false,
  creatable = false,
  createOptionText = "Create",
  onCreateOption,
  createOption,
  value,
  onChange,
  disabled = false,
  variant = "outline",
  maxVisibleTags = 1,
  inputProps
}: SelectInputProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(Array.isArray(value) ? value : value ? [value] : []);
  const [searchValue, setSearchValue] = useState("");
  const [dynamicOptions, setDynamicOptions] = useState<OptionLabelValue[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state with external value prop
  useEffect(() => {
    const newValues = Array.isArray(value) ? value : value ? [value] : [];
    // Only update if values actually changed to prevent infinite loops
    if (JSON.stringify([...newValues].sort()) !== JSON.stringify([...selectedValues].sort())) {
      setSelectedValues(newValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Check if options are grouped
  const isGrouped = useMemo(() => {
    return options.length > 0 && "options" in options[0];
  }, [options]);

  // Flatten grouped options for easier searching
  const flatOptions = useMemo(() => {
    const baseOptions = isGrouped ? (options as GroupedOptionLabelValue[]).flatMap(group => group.options) : options as OptionLabelValue[];

    // Combine with dynamically created options
    return [...baseOptions, ...dynamicOptions];
  }, [options, isGrouped, dynamicOptions]);

  // Combine all options (original + dynamic)
  const allOptions = useMemo(() => {
    if (isGrouped) {
      // For grouped options, add dynamic options to the first group or create a new group
      const groupedOptions = [...(options as GroupedOptionLabelValue[])];
      if (dynamicOptions.length > 0) {
        if (groupedOptions.length > 0) {
          // Add to first group
          groupedOptions[0] = {
            ...groupedOptions[0],
            options: [...groupedOptions[0].options, ...dynamicOptions]
          };
        } else {
          // Create new group for dynamic options
          groupedOptions.push({
            label: "Custom",
            value: "custom",
            options: dynamicOptions
          });
        }
      }
      return groupedOptions;
    } else {
      return [...(options as OptionLabelValue[]), ...dynamicOptions];
    }
  }, [options, dynamicOptions, isGrouped]);

  // Filter options based on search value
  const filteredOptions = useMemo(() => {
    if (!searchValue || !creatable) return allOptions;
    if (isGrouped) {
      return (allOptions as GroupedOptionLabelValue[]).map(group => ({
        ...group,
        options: group.options.filter(opt => opt.label.toLowerCase().includes(searchValue.toLowerCase()))
      })).filter(group => group.options.length > 0);
    } else {
      return (allOptions as OptionLabelValue[]).filter(opt => opt.label.toLowerCase().includes(searchValue.toLowerCase()));
    }
  }, [allOptions, searchValue, creatable, isGrouped]);

  // Check if search value would create a new option
  const canCreateNew = useMemo(() => {
    if (!creatable || !searchValue.trim()) return false;
    const searchLower = searchValue.toLowerCase();
    const existsInOptions = flatOptions.some(opt => opt.label.toLowerCase() === searchLower || opt.value.toLowerCase() === searchLower);
    return !existsInOptions;
  }, [creatable, searchValue, flatOptions]);
  const handleSelect = useCallback((optionValue: string) => {
    const isAlreadySelected = selectedValues.includes(optionValue);
    let newArray: string[] = [];
    if (isAlreadySelected) {
      newArray = selectedValues.filter(v => v !== optionValue);
    } else {
      newArray = multiple ? [...selectedValues, optionValue] : [optionValue];
    }
    setSelectedValues(newArray);

    // Return appropriate value type based on multiple prop
    const returnValue = multiple ? newArray : newArray[0] || "";
    onChange?.(returnValue);
    if (!multiple) {
      setSearchValue("");
    }
  }, [selectedValues, multiple, onChange]);
  const handleCreateOption = useCallback(async (inputValue: string) => {
    const createFn = onCreateOption || createOption;
    if (!createFn || !inputValue.trim()) return;
    try {
      const newOption = await createFn(inputValue.trim());

      // Add to dynamic options
      setDynamicOptions(prev => [...prev, newOption]);

      // Select the new option
      const newArray = multiple ? [...selectedValues, newOption.value] : [newOption.value];
      setSelectedValues(newArray);

      // Return appropriate value type based on multiple prop
      const returnValue = multiple ? newArray : newOption.value;
      onChange?.(returnValue);

      // Clear search and close popup
      setSearchValue("");
    } catch (error) {
      logger.error("Failed to create option:", error);
    }
  }, [onCreateOption, createOption, multiple, selectedValues, onChange]);
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValues([]);
    const returnValue = multiple ? [] : "";
    onChange?.(returnValue);
  }, [onChange, multiple]);
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);
  const displayedValues = useMemo(() => {
    if (selectedValues.length <= maxVisibleTags) {
      return selectedValues;
    }
    return selectedValues.slice(0, maxVisibleTags);
  }, [selectedValues, maxVisibleTags]);
  const extraCount = selectedValues.length > maxVisibleTags ? selectedValues.length - maxVisibleTags : 0;
  const renderOptions = () => {
    const items: React.ReactNode[] = [];
    if (isGrouped) {
      (filteredOptions as GroupedOptionLabelValue[]).forEach(group => {
        // Add a group header (disabled item)
        items.push(<ListBoxItem key={`header-${group.value}`} id={`header-${group.value}`} className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200 mb-1 cursor-default" isDisabled>
						{group.label}
					</ListBoxItem>);

        // Add group options
        group.options.forEach(opt => {
          const isSelected = selectedValues.includes(opt.value);
          items.push(<ListBoxItem key={`${group.value}-${opt.value}`} id={opt.value} onAction={() => handleSelect(opt.value)} className={cn("cursor-pointer rounded px-2 py-2 text-body flex items-center justify-between ml-2", "hover:bg-gray-100 focus:bg-gray-100 outline-none", isSelected && "bg-primary-100 text-primary-700")}>
							{opt.label}
							{isSelected && <span className="pl-1 text-primary-600">✔</span>}
						</ListBoxItem>);
        });
      });
    } else {
      (filteredOptions as OptionLabelValue[]).forEach(opt => {
        const isSelected = selectedValues.includes(opt.value);
        items.push(<ListBoxItem key={opt.value} id={opt.value} onAction={() => handleSelect(opt.value)} className={cn("cursor-pointer rounded px-2 py-2 text-body flex items-center justify-between", "hover:bg-gray-100 focus:bg-gray-100 outline-none", isSelected && "bg-primary-100 text-primary-700")}>
						{opt.label}
						{isSelected && <span className="pl-1 text-primary-600">✔</span>}
					</ListBoxItem>);
      });
    }

    // Add create option if applicable
    if (creatable && canCreateNew && searchValue.trim()) {
      items.push(<ListBoxItem key="create-new" id="create-new" onAction={() => handleCreateOption(searchValue)} className="cursor-pointer rounded px-2 py-2 text-body flex items-center gap-2 border-t border-gray-200 mt-1 pt-2 hover:bg-gray-100 focus:bg-gray-100 outline-none text-primary-600">
					<Plus size={16} />
					{createOptionText} &ldquo;{searchValue}&rdquo;
				</ListBoxItem>);
    }
    return items;
  };
  return <div className={cn("flex flex-col gap-1", className)}>
			{label && <Label className="text-title-medium">{label}</Label>}

			<MenuTrigger>
				<div className="relative w-full">
					{/* Trigger button */}
					<Button className={cn("flex items-center justify-between px-3 h-10 text-body w-full cursor-pointer", "disabled:opacity-50 disabled:cursor-not-allowed", errorMessage ? "border-red-500" : "border-gray-300", variant === "outline" ? "border rounded-lg focus:border-primary-600 focus:ring-2 focus:ring-primary-100" : "border-0 border-b focus:border-b-1 focus:border-primary-600", inputProps?.className)} isDisabled={disabled}>
						<div className="flex flex-wrap gap-1 truncate">
							{selectedValues.length > 0 ? <>
									{displayedValues.map((val: string) => {
                const optLabel = flatOptions?.find(o => o.value === val)?.label || val;
                return <span key={val} className={disabled ? "px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md" : "px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded-md"}>
												{optLabel}
											</span>;
              })}
									{extraCount > 0 && <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md">+{extraCount} more</span>}
								</> : <span className="text-gray-400">{placeholder}</span>}
						</div>
						<ChevronDown size={16} aria-hidden="true" />
					</Button>

					{/* Clear button */}
					{clearable && selectedValues.length > 0 && <button aria-label="Clear selection" type="button" onClick={handleClear} className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10">
							<X size={16} />
						</button>}

					{/* Popover */}
					{(isGrouped && (filteredOptions as GroupedOptionLabelValue[]).some(group => group.options.length > 0) || !isGrouped && (filteredOptions as OptionLabelValue[]).length > 0 || creatable && canCreateNew) && <Popover className="border border-gray-200 rounded-md shadow-lg bg-white p-1 mt-1">
							{/* Search input for creatable */}
							{creatable && <div className="p-2 border-b border-gray-200">
									<Input ref={inputRef} value={searchValue} onChange={handleSearchChange} placeholder="Search or create..." className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:border-primary-600 focus:ring-1 focus:ring-primary-100 outline-none" />
								</div>}

							<ListBox className="outline-none max-h-60 overflow-y-auto scrollbar-thin">{renderOptions()}</ListBox>
						</Popover>}
				</div>
			</MenuTrigger>

			{description && <Text slot="description" className="text-body-small text-gray-500">
					{description}
				</Text>}

			{errorMessage && <Text slot="errorMessage" className="text-body-small-medium text-destructive">
					{errorMessage}
				</Text>}
		</div>;
}
export { SelectInput, type GroupedOptionLabelValue, type OptionLabelValue };